import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Search, GraduationCap, Award, Crown, Loader2, Table2, LayoutGrid,
  AlertTriangle, Plus, Sparkles, ChevronDown, ChevronUp, X, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGE_COLORS = {
  'Gold Analyst': 'bg-[#C5A059]/20 text-[#C5A059] border-[#C5A059]/40',
  'Silver Researcher': 'bg-gray-300/10 text-gray-300 border-gray-400/40',
  'Bronze Learner': 'bg-orange-700/20 text-orange-400 border-orange-600/40',
  'Keep Learning': 'bg-white/5 text-gray-500 border-white/10',
};

const DailyArticleCard = ({ article }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#111111] border border-white/10 rounded-lg overflow-hidden" data-testid={`daily-article-${article.article_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left px-5 py-4 hover:bg-white/[0.02] transition-colors flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="bg-[#C5A059]/15 text-[#C5A059] text-[10px] px-2 py-0.5 rounded-full border border-[#C5A059]/30 font-semibold">{article.symbol}</span>
            <span className="text-gray-600 text-xs">{new Date(article.created_at).toLocaleDateString()}</span>
          </div>
          <h4 className="text-white font-semibold text-sm">{article.title}</h4>
        </div>
        {open ? <ChevronUp size={16} className="text-gray-500 flex-shrink-0" /> : <ChevronDown size={16} className="text-gray-500 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 text-gray-400 text-sm leading-relaxed whitespace-pre-wrap border-t border-white/5 pt-4">
          {article.content}
        </div>
      )}
    </div>
  );
};

const AddCryptoModal = ({ onClose, onAdded, getToken }) => {
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [generating, setGenerating] = useState(false);

  const submit = async () => {
    if (!name.trim() || !symbol.trim()) return toast.error('Name and symbol required');
    setGenerating(true);
    try {
      await axios.post(`${API}/research/cryptos`, { name: name.trim(), symbol: symbol.trim() },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(`${name} research profile generated`);
      onAdded();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="add-crypto-modal">
      <div className="bg-[#111111] border border-[#C5A059]/30 rounded-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Add New Crypto Project</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Project name (e.g. Polygon)"
            className="bg-black/50 border-white/10 text-white" data-testid="add-crypto-name" />
          <Input value={symbol} onChange={e => setSymbol(e.target.value)} placeholder="Symbol (e.g. POL)"
            className="bg-black/50 border-white/10 text-white" data-testid="add-crypto-symbol" />
          <p className="text-[11px] text-gray-600">The AI will generate the full 13-section research profile + quiz. Takes ~30 seconds.</p>
          <Button onClick={submit} disabled={generating}
            className="w-full bg-[#C5A059] text-black hover:bg-[#D4AF37] font-semibold" data-testid="add-crypto-submit">
            {generating ? <><Loader2 size={14} className="animate-spin mr-2" /> Generating research...</> : 'Generate Research Profile'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function ResearchHub() {
  const { user, isAuthenticated, getToken } = useAuth();
  const [cryptos, setCryptos] = useState([]);
  const [progress, setProgress] = useState({});
  const [dailyArticles, setDailyArticles] = useState([]);
  const [compare, setCompare] = useState([]);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('cards');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [genDaily, setGenDaily] = useState(false);

  const isAdmin = user?.role === 'admin';

  const fetchAll = async () => {
    try {
      const [cRes, dRes, cmpRes] = await Promise.all([
        axios.get(`${API}/research/cryptos`),
        axios.get(`${API}/research/daily?limit=5`),
        axios.get(`${API}/research/compare`),
      ]);
      setCryptos(cRes.data.cryptos);
      setDailyArticles(dRes.data.articles);
      setCompare(cmpRes.data.cryptos);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      axios.get(`${API}/research/progress`, { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(r => setProgress(r.data.progress)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const generateDaily = async () => {
    setGenDaily(true);
    try {
      await axios.post(`${API}/research/daily/generate`, {}, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Daily research article published');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenDaily(false);
    }
  };

  const featured = cryptos.find(c => c.featured);
  const filtered = cryptos.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.symbol.toLowerCase().includes(search.toLowerCase())
  );
  const completedCount = Object.values(progress).filter(p => p.completed).length;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="research-hub-page">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C5A059] to-[#D4AF37] flex items-center justify-center">
                <GraduationCap size={20} className="text-black" />
              </div>
              <h1 className="font-manrope font-bold text-3xl sm:text-4xl text-white">
                Crypto <span className="text-[#C5A059]">Research</span>
              </h1>
            </div>
            <p className="text-gray-400 text-sm max-w-2xl">
              Institutional-level blockchain research in simple language. Learn, take the quiz, earn your badge.
            </p>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <Button onClick={() => setShowAdd(true)} variant="outline"
                className="border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 rounded-sm text-xs"
                data-testid="admin-add-crypto-btn">
                <Plus size={14} className="mr-1" /> Add Crypto
              </Button>
              <Button onClick={generateDaily} disabled={genDaily} variant="outline"
                className="border-[#007AFF]/40 text-[#007AFF] hover:bg-[#007AFF]/10 rounded-sm text-xs"
                data-testid="admin-generate-daily-btn">
                {genDaily ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
                Publish Daily Report
              </Button>
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <div className="flex items-start gap-3 bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-6" data-testid="research-disclaimer">
          <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-200/70">
            Educational content only, not financial advice. No price predictions, no buy/sell recommendations —
            research, fundamentals, risks and market structure only.
          </p>
        </div>

        {/* Crypto of the Week */}
        {featured && (
          <Link to={`/research/${featured.slug}`}
            className="block bg-gradient-to-r from-[#1a1508] to-[#0D0D0D] border border-[#C5A059]/40 rounded-xl p-6 mb-8 hover:border-[#C5A059] transition-colors group"
            data-testid="crypto-of-the-week">
            <div className="flex items-center gap-2 mb-2">
              <Crown size={16} className="text-[#C5A059]" />
              <span className="text-[#C5A059] text-xs font-bold uppercase tracking-wider">Crypto of the Week</span>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-white font-manrope font-bold text-2xl group-hover:text-[#C5A059] transition-colors">
                  {featured.name} <span className="text-gray-500 text-lg">({featured.symbol})</span>
                </h2>
                <p className="text-gray-400 text-sm mt-1">{featured.tagline}</p>
              </div>
              <span className="text-[#C5A059] text-sm font-medium">Read the research →</span>
            </div>
          </Link>
        )}

        {/* Progress summary */}
        {isAuthenticated && cryptos.length > 0 && (
          <div className="flex items-center gap-3 mb-6 bg-[#111111] border border-white/10 rounded-lg px-4 py-3" data-testid="progress-tracker">
            <Award size={18} className="text-[#C5A059]" />
            <div className="flex-1">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-400">Your learning progress</span>
                <span className="text-[#C5A059] font-semibold">{completedCount} / {cryptos.length} lessons completed</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#C5A059] to-[#D4AF37] rounded-full transition-all"
                  style={{ width: `${(completedCount / cryptos.length) * 100}%` }} />
              </div>
            </div>
          </div>
        )}

        {/* Search + view toggle */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search any crypto (e.g. Bitcoin, HBAR, Chainlink)..."
              className="pl-11 bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C5A059] h-11"
              data-testid="research-search-input" />
          </div>
          <div className="flex gap-1 bg-[#111111] border border-white/10 rounded-lg p-1">
            <button onClick={() => setView('cards')} data-testid="view-cards-btn"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${view === 'cards' ? 'bg-[#C5A059] text-black' : 'text-gray-400 hover:text-white'}`}>
              <LayoutGrid size={13} /> Cards
            </button>
            <button onClick={() => setView('compare')} data-testid="view-compare-btn"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${view === 'compare' ? 'bg-[#C5A059] text-black' : 'text-gray-400 hover:text-white'}`}>
              <Table2 size={13} /> Compare
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 size={30} className="animate-spin text-[#C5A059]" /></div>
        ) : view === 'cards' ? (
          filtered.length === 0 ? (
            <div className="text-center py-12 mb-12 bg-[#111111] border border-dashed border-white/10 rounded-xl" data-testid="search-no-results">
              <Search className="mx-auto text-gray-600 mb-3" size={32} />
              <p className="text-gray-400 text-sm">No results for "<span className="text-white">{search}</span>"</p>
              <p className="text-gray-600 text-xs mt-1">We cover {cryptos.length} blockchains right now — more research profiles are added regularly.</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12" data-testid="crypto-cards-grid">
            {filtered.map(c => {
              const prog = progress[c.slug];
              return (
                <Link key={c.slug} to={`/research/${c.slug}`}
                  className="bg-[#111111] border border-white/10 rounded-xl p-5 hover:border-[#C5A059]/50 hover:shadow-[0_0_20px_rgba(197,160,89,0.08)] transition-all group hover-lift"
                  data-testid={`crypto-card-${c.slug}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#C5A059]/10 flex items-center justify-center font-bold text-[#C5A059] text-sm">
                        {c.symbol.slice(0, 4)}
                      </div>
                      <div>
                        <h3 className="text-white font-semibold group-hover:text-[#C5A059] transition-colors">{c.name}</h3>
                        <span className="text-gray-600 text-xs">{c.category}</span>
                      </div>
                    </div>
                    {c.featured && <Crown size={14} className="text-[#C5A059]" />}
                  </div>
                  <p className="text-gray-400 text-sm mb-3 line-clamp-2">{c.tagline}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-gray-600">{c.stats?.consensus}</span>
                    {prog ? (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${BADGE_COLORS[prog.badge] || BADGE_COLORS['Keep Learning']}`}>
                        {prog.badge} · {prog.score}/{prog.total}
                      </span>
                    ) : (
                      <span className="text-[11px] text-gray-600 inline-flex items-center gap-1"><BookOpen size={11} /> Start lesson</span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
          )
        ) : (
          <div className="overflow-x-auto mb-12 bg-[#111111] border border-white/10 rounded-xl" data-testid="compare-table">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="px-4 py-3 text-gray-400 font-medium">Blockchain</th>
                  <th className="px-4 py-3 text-gray-400 font-medium">Category</th>
                  <th className="px-4 py-3 text-gray-400 font-medium">Consensus</th>
                  <th className="px-4 py-3 text-gray-400 font-medium">TPS</th>
                  <th className="px-4 py-3 text-gray-400 font-medium">Avg Fee</th>
                  <th className="px-4 py-3 text-gray-400 font-medium">Launched</th>
                  <th className="px-4 py-3 text-gray-400 font-medium">Max Supply</th>
                  <th className="px-4 py-3 text-gray-400 font-medium">Smart Contracts</th>
                </tr>
              </thead>
              <tbody>
                {compare.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.symbol.toLowerCase().includes(search.toLowerCase())).map(c => (
                  <tr key={c.slug} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <Link to={`/research/${c.slug}`} className="text-white font-medium hover:text-[#C5A059] transition-colors">
                        {c.name} <span className="text-gray-600 text-xs">{c.symbol}</span>
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-gray-400">{c.category}</td>
                    <td className="px-4 py-3 text-gray-400">{c.stats?.consensus}</td>
                    <td className="px-4 py-3 text-[#C5A059] font-mono">{c.stats?.tps}</td>
                    <td className="px-4 py-3 text-gray-400 font-mono">{c.stats?.avg_fee}</td>
                    <td className="px-4 py-3 text-gray-400">{c.stats?.launch_year}</td>
                    <td className="px-4 py-3 text-gray-400">{c.stats?.max_supply}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.stats?.smart_contracts === 'Yes' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-white/5 text-gray-500'}`}>
                        {c.stats?.smart_contracts}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Daily research */}
        <div data-testid="daily-research-section">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={18} className="text-[#C5A059]" />
            <h2 className="text-white font-manrope font-bold text-xl">Daily Research Articles</h2>
          </div>
          {dailyArticles.length === 0 ? (
            <p className="text-gray-600 text-sm bg-[#111111] border border-dashed border-white/10 rounded-lg px-4 py-6 text-center">
              First daily research article publishing soon — generated automatically every 24 hours.
            </p>
          ) : (
            <div className="space-y-3">
              {dailyArticles.map(a => <DailyArticleCard key={a.article_id} article={a} />)}
            </div>
          )}
        </div>
      </div>

      {showAdd && <AddCryptoModal onClose={() => setShowAdd(false)} onAdded={fetchAll} getToken={getToken} />}
      <Footer />
    </div>
  );
}
